import React, { useState, useEffect } from "react";
import {
    Box,
    TextField,
    Button,
    CircularProgress,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    FormControl,
    FormLabel,
    RadioGroup,
    FormControlLabel,
    Radio,
    Select,
    MenuItem,
    Grid,
    IconButton,
    Modal,
} from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import axios from "axios";
import ProductDetailsCard from "../../components/Cards/ProductDetailsCard";
import USDAExpertSidebar from "../global/USDAExpertSidebar"; // Import the USDAExpertSidebar
import RecipeSuggestor from "../../components/scene-ai/RecipeSuggestor"; // Import RecipeSuggestor component
import figLogo from "../../assets/fig_CROP.png"; // FIG Logo

const USDAFoodSearch = () => {
    // Set default to light mode
    const lightTheme = createTheme({
        palette: {
            mode: "light",
            primary: { main: "#1976d2" },
            background: { default: "#f4f6f8", paper: "#ffffff" },
            text: { primary: "#000000", secondary: "#555555" },
        },
    });

    const [query, setQuery] = useState("");
    const [manufacturerQuery, setManufacturerQuery] = useState("");
    const [fdcIdQuery, setFdcIdQuery] = useState("");
    const [searchType, setSearchType] = useState("productName");
    const [dataSource, setDataSource] = useState("Branded");
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [isSidebarOpen, setIsSidebarOpen] = useState(true);
    const [recipeSuggestorOpen, setRecipeSuggestorOpen] = useState(false);
    const [selectedFdcId, setSelectedFdcId] = useState(null);

    const apiKey = "u5A7qQBkgjfxOEi0ec5xDWwU0WcSa3SDOg2dMPYG";
    const baseURL = "https://api.nal.usda.gov/fdc/v1/foods";

    const fetchTop10 = async () => {
        setLoading(true);
        setError("");
        try {
            const params = new URLSearchParams({
                api_key: apiKey,
                dataType: "Branded",
                pageSize: 10,
            });
            const url = `${baseURL}/search?${params.toString()}`;
            const response = await axios.get(url);
            setResults(response.data.foods || []);
        } catch (err) {
            setError("Failed to fetch initial data. Please try again later.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTop10();
    }, []);

    const handleSearch = async () => {
        let currentQuery = "";

        if (searchType === "productName") {
            currentQuery = query.trim();
        } else if (searchType === "manufacturerName") {
            currentQuery = manufacturerQuery.trim();
        } else if (searchType === "fdcId") {
            currentQuery = fdcIdQuery.trim();
        }

        if (!currentQuery) {
            setError("Please enter a search term.");
            return;
        }

        setLoading(true);
        setError("");

        try {
            let response;
            if (searchType === "fdcId") {
                const url = `${baseURL}/${currentQuery}?api_key=${apiKey}`;
                response = await axios.get(url);
                setResults([response.data]);
            } else {
                const params = new URLSearchParams({
                    api_key: apiKey,
                    query: currentQuery,
                    dataType: dataSource,
                    pageSize: 25,
                });
                const url = `${baseURL}/search?${params.toString()}`;
                response = await axios.get(url);
                setResults(response.data.foods || []);
            }
        } catch (err) {
            setError(
                searchType === "fdcId"
                    ? "FDC ID not found. Please check the ID and try again."
                    : "Failed to fetch data. Please try again later."
            );
        } finally {
            setLoading(false);
        }
    };

    const handleRowClick = (product) => {
        setSelectedProduct(product);
    };

    const handleSuggestRecipe = (product) => {
        setSelectedFdcId(product.fdcId);
        setRecipeSuggestorOpen(true);
    };

    const handleCloseRecipeSuggestor = () => {
        setRecipeSuggestorOpen(false);
        setSelectedFdcId(null);
    };

    const handleClose = () => {
        setSelectedProduct(null);
    };

    return (
        <ThemeProvider theme={lightTheme}>
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "row",
                    height: "100vh",
                    overflow: "hidden",
                }}
            >
                {/* Main Content Area */}
                <Box
                    sx={{
                        flex: isSidebarOpen ? 1 : 1.5, // Adjust width when Sidebar opens
                        padding: "20px",
                        backgroundColor: lightTheme.palette.background.default,
                        overflowY: "auto",
                    }}
                >
                    <IconButton
                        onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                        sx={{
                            position: "absolute",
                            top: "16px",
                            right: "16px",
                            backgroundColor: lightTheme.palette.primary.main,
                            borderRadius: "50%",
                            width: "48px",
                            height: "48px",
                        }}
                    >
                        <img
                            src={figLogo}
                            alt="FIG Logo"
                            style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                                borderRadius: "50%",
                            }}
                        />
                    </IconButton>

                    <Typography variant="h4" gutterBottom color={lightTheme.palette.text.primary}>
                        USDA FoodData Central Search
                    </Typography>

                    {/* Search Instructions */}
                    <Paper
                        sx={{
                            padding: "20px",
                            marginBottom: "20px",
                            backgroundColor: lightTheme.palette.background.paper,
                        }}
                    >
                        <Typography variant="h6" gutterBottom color={lightTheme.palette.text.primary}>
                            How to Use This Page
                        </Typography>
                        <Typography variant="body1" gutterBottom>
                            Use this page to search for products by name, manufacturer (brand), or FDC ID.
                        </Typography>
                    </Paper>

                    {/* Search Inputs (Augmented for FDC ID, brand, or product name) */}
                    <Box sx={{ marginBottom: "20px" }}>
                        <FormControl component="fieldset">
                            <FormLabel
                                component="legend"
                                sx={{ color: 'darkblue' }}  // <-- Set FormLabel text to dark blue
                            >
                                Search Type
                            </FormLabel>
                            <RadioGroup
                                row
                                aria-label="searchType"
                                name="searchType"
                                value={searchType}
                                onChange={(e) => setSearchType(e.target.value)}
                            >
                                <FormControlLabel
                                    value="productName"
                                    control={<Radio sx={{ color: 'darkblue' }} />} // Radio color
                                    label="Product Name"
                                    sx={{ color: 'darkblue' }}                      // Label color
                                />
                                <FormControlLabel
                                    value="manufacturerName"
                                    control={<Radio sx={{ color: 'darkblue' }} />}
                                    label="Brand"
                                    sx={{ color: 'darkblue' }}
                                />
                                <FormControlLabel
                                    value="fdcId"
                                    control={<Radio sx={{ color: 'darkblue' }} />}
                                    label="FDC ID"
                                    sx={{ color: 'darkblue' }}
                                />
                            </RadioGroup>
                        </FormControl>


                        {searchType === "productName" && (
                            <TextField
                                label="Product Name"
                                variant="outlined"
                                fullWidth
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                sx={{ marginTop: 2 }}
                            />
                        )}

                        {searchType === "manufacturerName" && (
                            <TextField
                                label="Brand Name"
                                variant="outlined"
                                fullWidth
                                value={manufacturerQuery}
                                onChange={(e) => setManufacturerQuery(e.target.value)}
                                sx={{ marginTop: 2 }}
                            />
                        )}

                        {searchType === "fdcId" && (
                            <TextField
                                label="FDC ID"
                                variant="outlined"
                                fullWidth
                                value={fdcIdQuery}
                                onChange={(e) => setFdcIdQuery(e.target.value)}
                                sx={{ marginTop: 2 }}
                            />
                        )}

                        <Box sx={{ marginTop: 2 }}>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleSearch}
                            >
                                Search
                            </Button>
                        </Box>
                    </Box>

                    {/* Loading Spinner / Error */}
                    {loading && <CircularProgress />}
                    {error && (
                        <Typography variant="body2" color="error" sx={{ marginTop: 2 }}>
                            {error}
                        </Typography>
                    )}

                    {/* Results */}
                    {results.length > 0 && (
                        <TableContainer
                            component={Paper}
                            sx={{
                                marginTop: "20px",
                                backgroundColor: lightTheme.palette.background.paper,
                            }}
                        >
                            <Table>
                                <TableHead>
                                    <TableRow sx={{ backgroundColor: lightTheme.palette.primary.main }}>
                                        <TableCell sx={{ color: lightTheme.palette.common.white }}>
                                            Description
                                        </TableCell>
                                        <TableCell sx={{ color: lightTheme.palette.common.white }}>
                                            Brand
                                        </TableCell>
                                        <TableCell sx={{ color: lightTheme.palette.common.white }}>
                                            FDC ID
                                        </TableCell>
                                        <TableCell sx={{ color: lightTheme.palette.common.white }}>
                                            Actions
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {results.map((product, index) => (
                                        <TableRow
                                            key={index}
                                            hover
                                            sx={{
                                                cursor: "pointer",
                                                backgroundColor:
                                                    index % 2 === 0
                                                        ? lightTheme.palette.action.hover
                                                        : "inherit",
                                            }}
                                        >
                                            <TableCell>{product.description}</TableCell>
                                            <TableCell>{product.brandOwner || "N/A"}</TableCell>
                                            <TableCell>{product.fdcId}</TableCell>
                                            <TableCell>
                                                <Box sx={{ display: "flex", gap: 1 }}>
                                                    <Button
                                                        variant="contained"
                                                        color="primary"
                                                        onClick={() => handleRowClick(product)} // Opens ProductDetailsCard
                                                    >
                                                        View Product Information
                                                    </Button>
                                                    <Button
                                                        variant="outlined"
                                                        color="secondary"
                                                        onClick={() => handleSuggestRecipe(product)} // Existing Suggest Recipe functionality
                                                    >
                                                        Suggest Recipe
                                                    </Button>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>

                    )}

                    {selectedProduct && (
                        <ProductDetailsCard
                            productData={selectedProduct}
                            onClose={handleClose}
                        />
                    )}
                </Box>

                {/* USDAExpertSidebar */}
                {isSidebarOpen && (
                    <Box
                        sx={{
                            width: "400px",
                            flexShrink: 0,
                            backgroundColor: lightTheme.palette.background.paper,
                            boxShadow: "-2px 0 4px rgba(0, 0, 0, 0.2)",
                        }}
                    >
                        <USDAExpertSidebar />
                    </Box>
                )}
            </Box>

            {/* RecipeSuggestor Modal */}
            <Modal
                open={recipeSuggestorOpen}
                onClose={handleCloseRecipeSuggestor}
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <Box
                    sx={{
                        backgroundColor: "white",
                        padding: 4,
                        borderRadius: 2,
                        boxShadow: 24,
                        width: "80%",
                        maxWidth: "800px",
                    }}
                >
                    <RecipeSuggestor fdcId={selectedFdcId} onClose={handleCloseRecipeSuggestor} />
                </Box>
            </Modal>
        </ThemeProvider>
    );
};

export default USDAFoodSearch;
